import { useEffect, useState } from 'react';
import type {
  AgreementPortalBinding,
  AgreementPortalCaiaInfo,
  AgreementPortalConnectBoosterData,
  AgreementPortalSettlement,
  AgreementPortalSettlementState,
} from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { DataState } from '../../components/ui/DataState.js';
import { SelectField, TextField } from '../../components/ui/Field.js';
import {
  useAgreementPortalBinding,
  useAgreementPortalCaia,
  useAgreementPortalConnectBooster,
  useAgreementPortalSettlement,
  useBindAgreementToPortal,
  useSetAgreementSettlementState,
} from './api.js';

/** Portal vendor-admin console; the tenant id (NetSuite Account ID) is appended. */
const VENDOR_ADMIN_URL = 'https://portal.nscatalyst.com/vendor_admin.php?tenant=';

interface AgreementPortalBillingTabProps {
  agreementId: string;
  canEdit: boolean;
}

/** Human label for a snake_case/camelCase key returned by the portal. */
function prettifyKey(key: string): string {
  const spaced = key.replace(/[_-]+/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

/** Renders the portal's payload as a key/value grid (primitives only) + the raw JSON. */
function PortalRecord({ record }: { record: unknown }): JSX.Element | null {
  if (!record || typeof record !== 'object') return null;
  const entries = Object.entries(record as Record<string, unknown>).filter(
    ([, value]) => value === null || typeof value !== 'object',
  );
  return (
    <div className="space-y-3">
      {entries.length ? (
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {entries.map(([key, value]) => (
            <div key={key}>
              <dt className="text-xs uppercase tracking-wide text-slate-500">{prettifyKey(key)}</dt>
              <dd className="mt-1 text-sm font-medium break-words text-slate-900">
                {formatValue(value)}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      <details>
        <summary className="cursor-pointer text-xs text-slate-500">Raw portal response</summary>
        <pre className="mt-2 max-h-72 overflow-auto rounded-lg bg-slate-900 p-3 text-xs text-slate-100">
          {JSON.stringify(record, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function BindingStatus({ binding }: { binding: AgreementPortalBinding }): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge tone={binding.bound ? 'green' : 'amber'}>
        {binding.bound ? 'Tenant bound' : 'Not bound'}
      </Badge>
      {binding.nsAccountId ? (
        <span className="text-sm text-slate-600">
          Tenant <span className="font-mono">{binding.nsAccountId}</span>
        </span>
      ) : (
        <Badge tone="red">No NetSuite Account ID</Badge>
      )}
      {!binding.configured ? <Badge tone="gray">Portal not configured</Badge> : null}
      {binding.nsAccountId ? (
        <a
          href={`${VENDOR_ADMIN_URL}${encodeURIComponent(binding.nsAccountId)}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          Vendor Admin
          <span aria-hidden="true">↗</span>
        </a>
      ) : null}
    </div>
  );
}

/** Caia (AI) provisioning + tier figures for the tenant. */
function CaiaInfoPanel({ caia }: { caia: AgreementPortalCaiaInfo }): JSX.Element {
  const figures: { label: string; value: string }[] = [
    { label: 'Display name', value: caia.displayName ?? '—' },
    { label: 'CAIA Tier', value: caia.tier ?? '—' },
    {
      label: 'CAIA Tier Dollar Limit',
      value:
        caia.monthlyDollarLimit != null
          ? `$${caia.monthlyDollarLimit.toLocaleString()}`
          : '—',
    },
    {
      label: 'CAIA Included Seats',
      value: caia.included != null ? String(caia.included) : '—',
    },
    {
      label: 'CAIA Purchased Seats',
      value: caia.purchased != null ? String(caia.purchased) : '—',
    },
    { label: 'CAIA Total Seats', value: caia.total != null ? String(caia.total) : '—' },
    { label: 'CAIA Used Seats', value: caia.used != null ? String(caia.used) : '—' },
  ];
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge tone={caia.provisioned ? 'green' : 'gray'}>
          {caia.provisioned ? 'AI provisioned' : 'AI not provisioned'}
        </Badge>
        {caia.tier ? <Badge tone="blue">{`Tier: ${caia.tier}`}</Badge> : null}
      </div>
      {caia.message ? <p className="text-sm text-amber-600">{caia.message}</p> : null}
      {caia.provisioned ? (
        <>
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {figures.map((figure) => (
              <div key={figure.label}>
                <dt className="text-xs uppercase tracking-wide text-slate-500">{figure.label}</dt>
                <dd className="mt-1 text-sm font-medium break-words text-slate-900">
                  {figure.value}
                </dd>
              </div>
            ))}
          </dl>
          <details>
            <summary className="cursor-pointer text-xs text-slate-500">
              Raw portal response
            </summary>
            <pre className="mt-2 max-h-72 overflow-auto rounded-lg bg-slate-900 p-3 text-xs text-slate-100">
              {JSON.stringify(caia.tenant, null, 2)}
            </pre>
          </details>
        </>
      ) : null}
    </div>
  );
}

function SettlementPanel({ settlement }: { settlement: AgreementPortalSettlement }): JSX.Element {
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge tone={settlement.found ? 'green' : 'gray'}>
          {settlement.found ? 'Settlement found' : 'No settlement'}
        </Badge>
        {settlement.state ? <SettlementStateBadge state={settlement.state} /> : null}
        <span className="text-slate-600">Period {settlement.period}</span>
      </div>
      {settlement.found ? (
        <div className="flex flex-wrap items-center gap-3">
          <PeakBadge label="Peak seats" value={settlement.peakSeats} />
          <PeakBadge label="Peak allocation credits" value={settlement.peakAllocationCredits} />
        </div>
      ) : null}
      {settlement.message ? <p className="text-sm text-amber-600">{settlement.message}</p> : null}
      {settlement.found ? <PortalRecord record={settlement.settlement} /> : null}
    </div>
  );
}

/** Portal state for the period — `ready` is still billable, `settled` is acknowledged. */
function SettlementStateBadge({ state }: { state: string }): JSX.Element {
  const tone = state === 'settled' ? 'green' : state === 'ready' ? 'amber' : 'gray';
  return <Badge tone={tone}>{state === 'settled' ? 'Settled' : state === 'ready' ? 'Ready to bill' : state}</Badge>;
}

/** Bold, green-when-positive badge for a billed peak figure. */
function PeakBadge({ label, value }: { label: string; value: number | null }): JSX.Element {
  const positive = value != null && value > 0;
  const tone = positive
    ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
    : 'border-slate-300 bg-white text-slate-600';
  return (
    <span
      className={`inline-flex items-baseline gap-2 rounded-lg border px-3 py-1.5 text-sm ${tone}`}
    >
      <span className="text-xs uppercase tracking-wide">{label}</span>
      <strong className="text-base font-bold">{value ?? '—'}</strong>
    </span>
  );
}

function ConnectBoosterPanel({
  data,
}: {
  data: AgreementPortalConnectBoosterData;
}): JSX.Element {
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge tone={data.ok ? 'green' : 'gray'}>{data.ok ? 'Data loaded' : 'No data'}</Badge>
        {data.records.length ? (
          <span className="text-slate-600">{data.records.length} recent records</span>
        ) : null}
      </div>
      {data.message ? <p className="text-sm text-amber-600">{data.message}</p> : null}
      {data.ok ? (
        <>
          <PortalRecord record={data.summary} />
          {data.records.length ? (
            <details>
              <summary className="cursor-pointer text-xs text-slate-500">
                Recent records ({data.records.length})
              </summary>
              <pre className="mt-2 max-h-72 overflow-auto rounded-lg bg-slate-900 p-3 text-xs text-slate-100">
                {JSON.stringify(data.records, null, 2)}
              </pre>
            </details>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/**
 * Portal Billing tab: binds this agreement to its Catalyst Portal tenant (resolved from the
 * customer's NetSuite Account ID) and shows the billing figures for its type — monthly
 * settlements (peak seats / allocation credits) for Managed Services, ConnectBooster totals
 * for Product Subscriptions on the ConnectBooster subtype.
 */
export function AgreementPortalBillingTab({
  agreementId,
  canEdit,
}: AgreementPortalBillingTabProps): JSX.Element {
  const bindingQuery = useAgreementPortalBinding(agreementId);
  const bindMutation = useBindAgreementToPortal(agreementId);
  const binding = bindingQuery.data;
  const mode = binding?.mode ?? 'none';

  const [agreementRef, setAgreementRef] = useState('');
  const [includedSeats, setIncludedSeats] = useState('0');
  const [seatUnitPrice, setSeatUnitPrice] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [bound, setBound] = useState(false);

  const [showSettlement, setShowSettlement] = useState(false);
  const [period, setPeriod] = useState<string | undefined>(undefined);
  const settlementQuery = useAgreementPortalSettlement(agreementId, period, {
    enabled: showSettlement && mode === 'managed_services',
  });
  const settlement = settlementQuery.data;

  const stateMutation = useSetAgreementSettlementState(agreementId);
  const [externalRef, setExternalRef] = useState('');
  const [pendingState, setPendingState] = useState<AgreementPortalSettlementState | null>(null);
  const [stateSaved, setStateSaved] = useState<AgreementPortalSettlementState | null>(null);

  const [showConnectBooster, setShowConnectBooster] = useState(false);
  const connectBoosterQuery = useAgreementPortalConnectBooster(agreementId, {
    enabled: showConnectBooster && mode === 'connectbooster',
  });
  const connectBooster = connectBoosterQuery.data;

  const caiaQuery = useAgreementPortalCaia(agreementId, {
    enabled: Boolean(binding?.configured && binding?.nsAccountId),
  });
  const caia = caiaQuery.data;

  useEffect(() => {
    if (!binding) return;
    setAgreementRef(binding.suggestedAgreementRef);
    const record =
      binding.binding && typeof binding.binding === 'object'
        ? (binding.binding as Record<string, unknown>)
        : null;
    const seats = record?.included_seats ?? record?.includedSeats;
    if (typeof seats === 'number') setIncludedSeats(String(seats));
    const ref = record?.agreement_ref ?? record?.agreementRef;
    if (typeof ref === 'string' && ref.trim()) setAgreementRef(ref);
    const price = record?.seat_unit_price ?? record?.seatUnitPrice;
    if (typeof price === 'number') setSeatUnitPrice(String(price));
    const currencyValue = record?.currency;
    if (typeof currencyValue === 'string' && currencyValue.trim()) setCurrency(currencyValue);
  }, [binding]);

  const canBind = Boolean(canEdit && binding?.configured && binding?.nsAccountId);

  const handleBind = (): void => {
    setBound(false);
    const seats = Number(includedSeats);
    bindMutation.mutate(
      {
        agreementRef: agreementRef.trim() || null,
        includedSeats: Number.isFinite(seats) ? Math.trunc(seats) : 0,
        seatUnitPrice: seatUnitPrice.trim() ? Number(seatUnitPrice) : null,
        currency: currency.trim() || null,
      },
      { onSuccess: () => setBound(true) },
    );
  };

  const bindError =
    bindMutation.error instanceof ApiError
      ? bindMutation.error.message
      : bindMutation.error
        ? 'Could not save the binding.'
        : null;

  const canSettle = Boolean(canEdit && binding?.configured && binding?.nsAccountId);

  const handleSettlementState = (nextState: AgreementPortalSettlementState): void => {
    if (!settlement) return;
    setStateSaved(null);
    setPendingState(nextState);
    stateMutation.mutate(
      {
        state: nextState,
        period: settlement.period,
        externalRef: nextState === 'settled' ? externalRef.trim() || null : null,
      },
      {
        onSuccess: (updated) => {
          setStateSaved(nextState);
          setPeriod(updated.period);
        },
        onSettled: () => setPendingState(null),
      },
    );
  };

  const stateError =
    stateMutation.error instanceof ApiError
      ? stateMutation.error.message
      : stateMutation.error
        ? 'Could not update the settlement state.'
        : null;

  const periodOptions = [
    { value: '', label: settlement?.period ? `Latest closed (${settlement.period})` : 'Latest closed month' },
    ...(settlement?.periods ?? []).map((entry) => ({
      value: entry.period,
      label: entry.label ? `${entry.period} — ${entry.label}` : entry.period,
    })),
  ];

  return (
    <div className="space-y-6">
      <Card>
        <DataState
          isLoading={bindingQuery.isLoading}
          isError={bindingQuery.isError}
          error={bindingQuery.error}
          onRetry={() => void bindingQuery.refetch()}
          isEmpty={false}
        >
          {binding ? (
            <div className="space-y-5">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Catalyst Portal tenant</h2>
                <p className="mt-0.5 text-sm text-slate-500">
                  Binds this agreement to the portal tenant matching the customer&apos;s NetSuite
                  Account ID. Once bound, the tenant is billed from PSA and appears in the
                  portal&apos;s monthly settlements.
                </p>
              </div>

              <BindingStatus binding={binding} />

              {binding.message ? (
                <p className="text-sm text-amber-600">{binding.message}</p>
              ) : null}

              {binding.bound ? <PortalRecord record={binding.binding} /> : null}

              {binding.configured && binding.nsAccountId ? (
                <div className="space-y-3 border-t border-slate-200 pt-4">
                  <h3 className="text-sm font-semibold text-slate-900">
                    {binding.bound ? 'Update binding' : 'Create binding'}
                  </h3>
                  <div className="grid gap-4 sm:grid-cols-4">
                    <TextField
                      label="Agreement reference"
                      value={agreementRef}
                      onChange={(e) => setAgreementRef(e.target.value)}
                      disabled={!canBind}
                    />
                    <TextField
                      label="Included seats"
                      type="number"
                      min={0}
                      step={1}
                      value={includedSeats}
                      onChange={(e) => setIncludedSeats(e.target.value)}
                      disabled={!canBind}
                    />
                    <TextField
                      label="Seat unit price"
                      type="number"
                      min={0}
                      step="0.01"
                      value={seatUnitPrice}
                      onChange={(e) => setSeatUnitPrice(e.target.value)}
                      placeholder="Optional"
                      disabled={!canBind}
                    />
                    <TextField
                      label="Currency"
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      placeholder="USD"
                      disabled={!canBind}
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={handleBind}
                      isLoading={bindMutation.isPending}
                      disabled={!canBind}
                    >
                      {binding.bound ? 'Update binding' : 'Create binding'}
                    </Button>
                    {!canEdit ? (
                      <span className="text-xs text-slate-500">
                        You need edit access on agreements to change the binding.
                      </span>
                    ) : null}
                  </div>
                  {bindError ? <p className="text-sm text-rose-600">{bindError}</p> : null}
                  {bound ? <p className="text-sm text-emerald-600">Binding saved.</p> : null}
                </div>
              ) : null}

              {binding.configured && binding.nsAccountId ? (
                <div className="space-y-3 border-t border-slate-200 pt-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="text-sm font-semibold text-slate-900">CAIA info</h3>
                    <Button
                      variant="secondary"
                      onClick={() => void caiaQuery.refetch()}
                      isLoading={caiaQuery.isFetching}
                    >
                      Refresh
                    </Button>
                  </div>
                  <DataState
                    isLoading={caiaQuery.isLoading}
                    isError={caiaQuery.isError}
                    error={caiaQuery.error}
                    onRetry={() => void caiaQuery.refetch()}
                    isEmpty={false}
                  >
                    {caia ? <CaiaInfoPanel caia={caia} /> : null}
                  </DataState>
                </div>
              ) : null}
            </div>
          ) : null}
        </DataState>
      </Card>

      {mode === 'managed_services' ? (
        <Card>
          <div className="space-y-4">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Settlement</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                The portal&apos;s frozen billable figures (peak seats and allocation credits) for a
                closed month. Mark a period settled once it has been billed — settled periods drop
                out of the portal&apos;s next ready-to-bill pull.
              </p>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              {showSettlement && periodOptions.length > 1 ? (
                <div className="w-72">
                  <SelectField
                    label="Period"
                    value={period ?? ''}
                    onChange={(e) => setPeriod(e.target.value || undefined)}
                    options={periodOptions}
                  />
                </div>
              ) : null}
              <Button
                variant="secondary"
                onClick={() => {
                  if (showSettlement) {
                    void settlementQuery.refetch();
                  } else {
                    setShowSettlement(true);
                  }
                }}
                isLoading={settlementQuery.isFetching && showSettlement}
                disabled={!binding?.configured || !binding?.nsAccountId}
              >
                {showSettlement ? 'Refresh settlement' : 'Show settlement'}
              </Button>
            </div>

            {showSettlement ? (
              <DataState
                isLoading={settlementQuery.isLoading}
                isError={settlementQuery.isError}
                error={settlementQuery.error}
                onRetry={() => void settlementQuery.refetch()}
                isEmpty={false}
              >
                {settlement ? <SettlementPanel settlement={settlement} /> : null}
              </DataState>
            ) : null}

            {showSettlement && settlement?.found ? (
              <div className="space-y-3 border-t border-slate-200 pt-4">
                <h3 className="text-sm font-semibold text-slate-900">
                  Settlement state for {settlement.period}
                </h3>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="w-72">
                    <TextField
                      label="Invoice reference (optional)"
                      value={externalRef}
                      onChange={(e) => setExternalRef(e.target.value)}
                      placeholder="e.g. INV-2026-0042"
                      disabled={!canSettle}
                    />
                  </div>
                  <Button
                    onClick={() => handleSettlementState('settled')}
                    isLoading={stateMutation.isPending && pendingState === 'settled'}
                    disabled={!canSettle || settlement.state === 'settled'}
                  >
                    Mark settled
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => handleSettlementState('ready')}
                    isLoading={stateMutation.isPending && pendingState === 'ready'}
                    disabled={!canSettle || settlement.state === 'ready'}
                  >
                    Mark ready
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Marking settled acknowledges the period on the portal (idempotent). Marking ready
                  reverts the acknowledgement so the period can be billed again.
                </p>
                {stateError ? <p className="text-sm text-rose-600">{stateError}</p> : null}
                {stateSaved ? (
                  <p className="text-sm text-emerald-600">
                    Period {settlement.period} marked {stateSaved}.
                  </p>
                ) : null}
                {!canEdit ? (
                  <span className="text-xs text-slate-500">
                    You need edit access on agreements to change the settlement state.
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </Card>
      ) : null}

      {mode === 'connectbooster' ? (
        <Card>
          <div className="space-y-4">
            <div>
              <h2 className="text-base font-semibold text-slate-900">ConnectBooster</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                Billing totals and recent records the portal holds for this customer&apos;s
                ConnectBooster account. Read-only.
              </p>
            </div>

            <Button
              variant="secondary"
              onClick={() => {
                if (showConnectBooster) {
                  void connectBoosterQuery.refetch();
                } else {
                  setShowConnectBooster(true);
                }
              }}
              isLoading={connectBoosterQuery.isFetching && showConnectBooster}
              disabled={!binding?.configured || !binding?.nsAccountId}
            >
              {showConnectBooster ? 'Refresh ConnectBooster data' : 'Show ConnectBooster data'}
            </Button>

            {showConnectBooster ? (
              <DataState
                isLoading={connectBoosterQuery.isLoading}
                isError={connectBoosterQuery.isError}
                error={connectBoosterQuery.error}
                onRetry={() => void connectBoosterQuery.refetch()}
                isEmpty={false}
              >
                {connectBooster ? <ConnectBoosterPanel data={connectBooster} /> : null}
              </DataState>
            ) : null}
          </div>
        </Card>
      ) : null}
    </div>
  );
}
